/**
 * Token Expiration Monitor Component
 * Monitors JWT token expiration and shows warning modal
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import TokenRefreshModal from './TokenRefreshModal';
import PhoneVerificationModal from '../idv/PhoneVerificationModal';
import EmailVerificationModal from '../idv/EmailVerificationModal';
import { revokeLapsedVerifications } from '@/services/iam';
import { useUserContext } from '@/modules/user/useUserContext';
import { ClaimsLogic, CLAIMS_LEVEL, useTokenExpiration } from '@/modules/idv';

const TokenExpirationMonitor: React.FC = () => {
  const location = useLocation();
  const { refreshUserContext, claims } = useUserContext();
  const { showWarning, timeUntilExpiration, isExpired } = useTokenExpiration();
  const [showModal, setShowModal] = useState(false);
  const [expiredHandled, setExpiredHandled] = useState(false);
  const [refreshJustCompleted, setRefreshJustCompleted] = useState(false);
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] =
    useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] =
    useState(false);

  // Show modal when warning is triggered (but not if we just completed a refresh)
  useEffect(() => {
    if (refreshJustCompleted) {
      // Don't show modal immediately after refresh - wait for token to be re-checked
      // Explicitly ensure modal is closed during refresh
      setShowModal(false);
      return;
    }
    if (
      showWarning &&
      timeUntilExpiration !== undefined &&
      timeUntilExpiration > 0
    ) {
      setShowModal(true);
    } else if (!showWarning) {
      setShowModal(false);
      setRefreshJustCompleted(false); // Reset flag when warning clears
    }
  }, [showWarning, timeUntilExpiration, refreshJustCompleted]);

  const handleTokenExpired = useCallback(async () => {
    // Drop the lapsed claim(s) for a fresh token that keeps any still-live one
    try {
      await revokeLapsedVerifications(claims);
      refreshUserContext();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        'Failed to revoke lapsed verifications after token expiration:',
        error
      );
    }
  }, [claims, refreshUserContext]);

  // Handle token expiration
  useEffect(() => {
    if (isExpired && !expiredHandled) {
      handleTokenExpired();
      setExpiredHandled(true);
    } else if (!isExpired) {
      setExpiredHandled(false);
    }
  }, [isExpired, expiredHandled, handleTokenExpired]);

  const handleRefresh = () => {
    // Close modal immediately and prevent it from reopening during IDV flow
    setShowModal(false);
    setRefreshJustCompleted(true);

    // Reset the flag after at least 60 seconds to give user time to complete IDV
    // The actual token will be refreshed when IDV completes, which will naturally
    // reset the timer, but we prevent the warning from showing again during IDV
    setTimeout(() => {
      setRefreshJustCompleted(false);
    }, 60000); // Give at least 60 seconds for IDV flow
  };

  const handleOpenVerification = () => {
    if (ClaimsLogic.verificationToRenew(claims) === CLAIMS_LEVEL.EMAIL) {
      setShowEmailVerificationModal(true);
    } else {
      setShowPhoneVerificationModal(true);
    }
  };

  const handlePhoneVerificationClose = (_reason?: string) => {
    setShowPhoneVerificationModal(false);
  };

  const handleEmailVerificationClose = (_reason?: string) => {
    setShowEmailVerificationModal(false);
  };

  const handleExpired = () => {
    setShowModal(false);
    setExpiredHandled(true);
  };

  // Calculate seconds until expiration for the modal
  const secondsUntilExpiration = timeUntilExpiration
    ? Math.max(0, Math.ceil(timeUntilExpiration / 1000))
    : 60;

  return (
    <>
      <TokenRefreshModal
        isOpen={showModal}
        onRefresh={handleRefresh}
        onExpired={handleExpired}
        timeRemaining={secondsUntilExpiration}
        onOpenVerification={handleOpenVerification}
      />
      <PhoneVerificationModal
        isOpen={showPhoneVerificationModal}
        onClose={handlePhoneVerificationClose}
        continuePath={location.pathname}
      />
      <EmailVerificationModal
        isOpen={showEmailVerificationModal}
        onClose={handleEmailVerificationClose}
        continuePath={location.pathname}
      />
    </>
  );
};

export default TokenExpirationMonitor;
